import { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { useNavigate, useParams } from "react-router-dom";
import {
  ChevronLeft,
  Users,
  User,
  Mail,
  Phone,
  Check,
  AlertCircle,
  Ticket,
} from "lucide-react";
import { toast } from "react-toastify";

const STORAGE_KEY_PREFIX = "eventBooking_agent_";
const BASE_PATH = "/event-media-sales/event-details";

export default function AgentEventGuestDetails() {
  const navigate = useNavigate();
  const { slug } = useParams();
  const [eventData, setEventData] = useState(null);
  const [ticketSelection, setTicketSelection] = useState(null);
  const [selectedClient, setSelectedClient] = useState(null);
  const [numberOfGuests, setNumberOfGuests] = useState(0);
  const [guests, setGuests] = useState([]);
  const [errors, setErrors] = useState({});

  const storageKey = slug ? `${STORAGE_KEY_PREFIX}${slug}` : null;

  useEffect(() => {
    if (!storageKey || !slug) {
      navigate("/event-media-sales");
      return;
    }

    const saved = sessionStorage.getItem(storageKey);
    if (!saved) {
      toast.error("Missing booking information");
      navigate(`${BASE_PATH}/${slug}/book`);
      return;
    }

    try {
      const parsed = JSON.parse(saved);
      if (!parsed.eventData || !parsed.ticketSelection) {
        toast.error("Invalid booking data");
        navigate(`${BASE_PATH}/${slug}/book`);
        return;
      }

      setEventData(parsed.eventData);
      setTicketSelection(parsed.ticketSelection);
      if (parsed.selectedClient) setSelectedClient(parsed.selectedClient);

      const guestCount = (parsed.ticketSelection.quantity || 1) - 1;
      setNumberOfGuests(guestCount);

      if (parsed.guests && parsed.guests.length > 0) {
        setGuests(parsed.guests);
      } else {
        setGuests(
          Array.from({ length: guestCount }, () => ({
            name: "",
            email: "",
            phone: "",
          }))
        );
      }
    } catch (e) {
      console.error("Error loading booking data:", e);
      toast.error("Failed to load booking data");
      navigate(`${BASE_PATH}/${slug}/book`);
    }
  }, [slug, storageKey, navigate]);

  const validateGuest = (guest, index) => {
    const guestErrors = {};
    if (!guest.name?.trim()) guestErrors.name = "Name is required";
    if (!guest.email?.trim()) {
      guestErrors.email = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(guest.email)) {
      guestErrors.email = "Invalid email format";
    }
    if (!guest.phone?.trim()) {
      guestErrors.phone = "Phone number is required";
    } else if (!/^0\d{9}$/.test(guest.phone.replace(/\s/g, ""))) {
      guestErrors.phone = "Invalid phone number (use format: 0XXXXXXXXX)";
    }
    return guestErrors;
  };

  const handleGuestChange = (index, field, value) => {
    const newGuests = [...guests];
    newGuests[index] = { ...newGuests[index], [field]: value };
    setGuests(newGuests);

    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        parsed.guests = newGuests;
        sessionStorage.setItem(storageKey, JSON.stringify(parsed));
      } catch (err) {
        console.error("Error auto-saving guest data:", err);
      }
    }

    if (errors[`guest${index}`]?.[field]) {
      const newErrors = { ...errors };
      const ge = newErrors[`guest${index}`];
      if (ge) {
        delete ge[field];
        if (Object.keys(ge).length === 0) delete newErrors[`guest${index}`];
      }
      setErrors(newErrors);
    }
  };

  const handleContinue = () => {
    const newErrors = {};
    guests.forEach((guest, index) => {
      const guestErrors = validateGuest(guest, index);
      if (Object.keys(guestErrors).length > 0) newErrors[`guest${index}`] = guestErrors;
    });

    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      toast.error("Please fill in all required guest information");
      return;
    }

    const saved = sessionStorage.getItem(storageKey);
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        parsed.guests = guests;
        sessionStorage.setItem(storageKey, JSON.stringify(parsed));
      } catch (err) {
        console.error("Error updating booking data:", err);
      }
    }

    navigate(`${BASE_PATH}/${slug}/event-payment`, {
      state: { selectedClient: selectedClient ?? null },
    });
  };

  if (!eventData || !ticketSelection) return null;

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-50 via-white to-gray-50">
      <div className="sticky top-0 z-20 bg-gradient-to-br from-emerald-600 via-teal-600 to-cyan-700 shadow-2xl">
        <div className="relative px-4 py-8 sm:px-6 lg:px-8">
          <div
            className="absolute inset-0 opacity-10"
            style={{
              backgroundImage: "radial-gradient(circle at 2px 2px, white 1px, transparent 0)",
              backgroundSize: "40px 40px",
            }}
          />
          <div className="relative mx-auto max-w-6xl">
            <motion.button
              onClick={() => navigate(`${BASE_PATH}/${slug}/book`)}
              whileHover={{ x: -4, scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              className="flex gap-2 items-center px-5 py-2.5 mb-6 text-sm font-semibold text-white bg-white/20 backdrop-blur-sm rounded-xl border border-white/30 shadow-lg transition-all hover:bg-white/30 hover:shadow-xl"
            >
              <ChevronLeft size={18} />
              Back
            </motion.button>
            <div className="flex gap-4 items-center">
              <div className="flex justify-center items-center w-16 h-16 rounded-2xl border-2 shadow-2xl backdrop-blur-md bg-white/20 border-white/30">
                <Users size={32} className="text-white" />
              </div>
              <div className="flex-1">
                <h1 className="text-2xl font-extrabold text-white drop-shadow-lg sm:text-3xl">
                  Guest information
                </h1>
                <p className="mt-1 text-sm text-white sm:text-base">
                  Please provide details for your guests
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="px-4 py-8 sm:px-6 lg:px-8">
        <div className="mx-auto max-w-6xl">
          <div className="grid grid-cols-1 gap-8 lg:grid-cols-3">
            <div className="lg:col-span-2">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                className="flex gap-3 items-center p-4 mb-8 bg-gradient-to-r from-emerald-50 to-teal-50 rounded-2xl border-2 border-emerald-200 shadow-md"
              >
                <div className="flex justify-center items-center w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full shadow-lg">
                  <span className="text-lg font-bold text-white">2</span>
                </div>
                <div>
                  <p className="text-sm font-bold text-emerald-900">Step 2 of 3</p>
                  <p className="text-xs text-emerald-700">Enter guest information</p>
                </div>
              </motion.div>

              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.1 }}
                className="flex gap-3 items-start p-5 mb-8 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-2xl border-2 border-blue-200"
              >
                <AlertCircle size={24} className="flex-shrink-0 text-blue-600 mt-0.5" />
                <div>
                  <p className="mb-1 font-bold text-blue-900">
                    {numberOfGuests} guest{numberOfGuests > 1 ? "s" : ""} required
                  </p>
                  <p className="text-sm text-blue-700">
                    You're purchasing {ticketSelection.quantity} tickets. Please provide information for each additional guest.
                  </p>
                </div>
              </motion.div>

              <div className="space-y-6">
                {guests.map((guest, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: 0.2 + index * 0.1 }}
                    className="p-6 bg-white rounded-2xl border-2 border-gray-200 shadow-lg"
                  >
                    <div className="flex gap-3 items-center pb-4 mb-6 border-b-2 border-gray-100">
                      <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md">
                        <User size={24} className="text-white" />
                      </div>
                      <div>
                        <h3 className="text-lg font-bold text-gray-900">Guest {index + 1}</h3>
                        <p className="text-sm text-gray-600">All fields are required</p>
                      </div>
                    </div>

                    <div className="space-y-4">
                      <div>
                        <label className="block mb-2 text-sm font-bold text-gray-700">Full name *</label>
                        <div className="relative">
                          <User size={18} className="absolute top-3.5 left-4 text-gray-400" />
                          <input
                            type="text"
                            value={guest.name}
                            onChange={(e) => handleGuestChange(index, "name", e.target.value)}
                            placeholder="Enter guest's full name"
                            className={`py-3 pr-4 pl-12 w-full text-gray-900 bg-gray-50 rounded-xl border-2 transition-all outline-none ${ errors[`guest${index}`]?.name
                                ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                : "border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                            }`}
                          />
                        </div>
                        {errors[`guest${index}`]?.name && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex gap-2 items-center mt-2 text-sm text-red-600"
                          >
                            <AlertCircle size={14} />
                            {errors[`guest${index}`].name}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-bold text-gray-700">Email address *</label>
                        <div className="relative">
                          <Mail size={18} className="absolute top-3.5 left-4 text-gray-400" />
                          <input
                            type="email"
                            value={guest.email}
                            onChange={(e) => handleGuestChange(index, "email", e.target.value)}
                            placeholder="guest@example.com"
                            className={`py-3 pr-4 pl-12 w-full text-gray-900 bg-gray-50 rounded-xl border-2 transition-all outline-none ${ errors[`guest${index}`]?.email
                                ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                : "border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                            }`}
                          />
                        </div>
                        {errors[`guest${index}`]?.email && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex gap-2 items-center mt-2 text-sm text-red-600"
                          >
                            <AlertCircle size={14} />
                            {errors[`guest${index}`].email}
                          </motion.p>
                        )}
                      </div>

                      <div>
                        <label className="block mb-2 text-sm font-bold text-gray-700">Phone number *</label>
                        <div className="relative">
                          <Phone size={18} className="absolute top-3.5 left-4 text-gray-400" />
                          <input
                            type="tel"
                            value={guest.phone}
                            onChange={(e) => handleGuestChange(index, "phone", e.target.value)}
                            placeholder="0XXXXXXXXX"
                            className={`py-3 pr-4 pl-12 w-full text-gray-900 bg-gray-50 rounded-xl border-2 transition-all outline-none ${ errors[`guest${index}`]?.phone
                                ? "border-red-300 focus:border-red-500 focus:ring-2 focus:ring-red-200"
                                : "border-gray-200 focus:border-emerald-500 focus:ring-2 focus:ring-emerald-200"
                            }`}
                          />
                        </div>
                        {errors[`guest${index}`]?.phone && (
                          <motion.p
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            className="flex gap-2 items-center mt-2 text-sm text-red-600"
                          >
                            <AlertCircle size={14} />
                            {errors[`guest${index}`].phone}
                          </motion.p>
                        )}
                      </div>

                      {guest.name && guest.email && guest.phone &&
                        !errors[`guest${index}`]?.name &&
                        !errors[`guest${index}`]?.email &&
                        !errors[`guest${index}`]?.phone && (
                          <motion.div
                            initial={{ opacity: 0, scale: 0.9 }}
                            animate={{ opacity: 1, scale: 1 }}
                            className="flex gap-2 items-center p-3 bg-green-50 rounded-lg border border-green-200"
                          >
                            <Check size={18} className="text-green-600" />
                            <span className="text-sm font-medium text-green-700">Guest information complete</span>
                          </motion.div>
                        )}
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>

            <div className="lg:col-span-1">
              <motion.div
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.2 }}
                className="sticky top-32 p-6 bg-gradient-to-br from-white to-gray-50 rounded-2xl border-2 border-gray-200 shadow-xl"
              >
                <div className="flex gap-3 items-center pb-6 mb-6 border-b-2 border-gray-200">
                  <div className="flex justify-center items-center w-12 h-12 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-xl shadow-md">
                    <Ticket size={24} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Booking summary</h3>
                </div>

                <div className="space-y-4">
                  <div className="p-4 bg-gradient-to-br from-emerald-50 to-teal-50 rounded-xl border border-emerald-200">
                    <p className="mb-2 text-xs font-bold tracking-wide text-emerald-700 uppercase">Event</p>
                    <p className="text-sm font-bold leading-tight text-emerald-900">{eventData.event_title}</p>
                  </div>

                  <div className="p-4 bg-white rounded-xl border border-gray-200">
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">Ticket type</span>
                        <span className="text-sm font-bold text-gray-900">{ticketSelection.ticketType.name}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-sm font-medium text-gray-600">Quantity</span>
                        <span className="text-sm font-bold text-gray-900">{ticketSelection.quantity}</span>
                      </div>
                      <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                        <span className="text-sm font-bold text-gray-700">Total amount</span>
                        <span className="text-lg font-bold text-gray-900">
                          GH₵ {ticketSelection.totalAmount}
                        </span>
                      </div>
                    </div>
                  </div>

                  <div className="p-4 bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl border border-blue-200">
                    <p className="mb-3 text-xs font-bold text-blue-700 uppercase">Progress</p>
                    <div className="space-y-2">
                      {guests.map((guest, index) => {
                        const isComplete = guest.name && guest.email && guest.phone;
                        return (
                          <div key={index} className="flex gap-2 items-center">
                            <div
                              className={`flex justify-center items-center w-6 h-6 rounded-full ${
                                isComplete ? "bg-green-500" : "bg-gray-300"
                              }`}
                            >
                              {isComplete ? (
                                <Check size={14} className="text-white" />
                              ) : (
                                <span className="text-xs font-bold text-white">{index + 1}</span>
                              )}
                            </div>
                            <span className={`text-sm ${isComplete ? "font-medium text-green-700" : "text-gray-600"}`}>
                              Guest {index + 1} {isComplete && "✓"}
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>

                  <motion.button
                    type="button"
                    onClick={handleContinue}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                    className="px-6 py-4 w-full text-base font-bold text-white bg-gradient-to-r from-emerald-500 to-teal-600 rounded-xl shadow-xl transition-all hover:from-emerald-600 hover:to-teal-700 hover:shadow-2xl"
                  >
                    Continue to payment
                  </motion.button>
                </div>
              </motion.div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
